import { BankRequirements, CountryInfo } from './payment.models';

export interface CreatePatientResponse {
  patientId: string;
  patientTrialId: string;
}

export interface PatientPaymentInfoUpdateRequest {
  patientTrialId: string;
  defaultPaymentMethod: PaymentMethod;
  bankTransferMethod: BankTransferMethod;
  caxtonWalletName: string;
  caxtonWalletId: number;
  title: string;
  firstName: string;
  lastName: string;
  beneficiaryDetails: CaxtonPatientPaymentInfoRequest;
  initialBankCoverFee: number;
  bankCoverFeeCurrency: string;
  accountOwnerAddress: string;
  bankCoverFees: BankCoverFee[];
}

export interface UpdatePatientCaxtonBeneficiaryRequest {
  patientTrialId: string;
  caxtonBeneficiaryId: string;
}

export interface CaxtonPatientPaymentInfoRequest {
  country: string;
  currency: string;
  iban: string;
  swift: string;
  sortCode: string;
  accountNumber: string;
}

export interface AssignAndMoveWalletRequest {
  patientTrialId: string;
  walletId: string;
  walletName: string;
  walletAccountHolder: string;
  walletIsOnChildSubsidiary: boolean;
}

export interface AssignWalletResult {
  walletName: string;
  walletId: string;
  cardAlreadyActive: boolean;
}

export interface AssignWalletOnParentRequest {
  caxtonWalletName: string;
  caxtonWalletId: string;
  trialId: string;
  patientTrialId: string;
}

export interface AvailableWalletsResponse {
  walletAccountHolder: string;
  walletName: string;
  walletId: string;
  walletIsOnChildSubsidiary: boolean;
}

export interface ArchiveWalletRequest {
  patientTrialId: string;
  walletId: string;
  walletIsOnChildSubsidiary: boolean;
}

export interface CreateTestWalletRequest {
  walletName: string;
}

export interface CreateTestWalletResult {
  walletId: string;
  walletName: string;
}

export interface PatientPaymentInfo {
  defaultPaymentMethod: PaymentMethod;
  bankTransferMethod: BankTransferMethod;
  caxtonWalletName: string;
  caxtonWalletId: string;
}

export interface FullPatientPaymentInfo extends PatientPaymentInfo {
  caxtonBeneficiaryId: string;
  trialNmibleEnabled: boolean;
  trialVirtualCardInAppEnabled: boolean;
  subsidiaryLinkedToTrial: boolean;
  trialCaxtonEnabled: boolean;
  caxtonOnlineAccessGranted: boolean;
  caxtonCardsActivated: boolean;
  caxtonCardsStolen: boolean;
  caxtonCardsLost: boolean;
  caxtonCardsBlocked: boolean;
  firstName: string;
  lastName: string;
  title: string;
  previousBeneficiaryIds: string[];
  beneficiaryDetails: BeneficiaryDetails;
  countries: CountryInfo[];
  bankRequirements: BankRequirements;
  initialBankCoverFee: number;
  bankCoverFeeCurrency: string;
  accountOwnerAddress: string;
  bankCoverFees: BankCoverFee[];
  nameCurrencyBeneficiaryExists: boolean;
  allSubsidiaryBeneficiaries: BeneficiaryDetails[];
  existingMatchingBeneficiary: BeneficiaryDetails;
  hasSelectedNmibleCard: boolean;
  hasSelectedNmibleBank: boolean;
  email: string;
}

export interface CreatePatientRegistrationRequest {
  siteContactId: string;
  patientRegistrationCode: string;
  siteId: string;
  trialId: string;
  investigatorName: string;
  patientFirstName: string;
  patientLastName: string;
  patientNumber: string;
  yearOfBirth: number;
  address: string;
  phoneNumber: string;
  email: string;
  contactName: string;
  contactPhoneNumber: string;
  contactRelationship: string;
  signedRegistrationFilename: string;
  bankDetailsFilename: string;
  miscFilenames: string;
  screeningDate: Date;
  comments: string;
}

export interface GetSiteContactTrialsRequest {
  siteContactId: string;
  patientRegistrationCode: string;
}

export interface UploadSignatureResponse {
  signedRegistrationFilename: string;
}

export interface SiteContactTrialsResult {
  trialId: string;
  publicTrialCode: string;
  sites: TrialSiteResult[];
}

export interface SiteContactTrialsResponse {
  logoUrl: string | null;
  trials: SiteContactTrialsResult[];
}

export interface TrialSiteResult {
  siteId: string;
  siteText: string;
}

export interface PatientRegistrationListViewModel {
  id: string;
  patientName: string;
  patientFirstName: string;
  patientLastName: string;
  patientNumber: string;
  patientEmail: string;
  siteNumber: string;
  siteId: string;
  trialCode: string;
  trialId: string;
  state: string;
  created: Date;
  actioned?: Date;
  screeningDate: Date;
  comments: string;
  cardNumber: string;
  language: string;
}

export interface PatientRegistrationViewModel extends PatientRegistrationListViewModel {
  trialCode: string;
  investigatorName: string;
  email: string;
  formFillerEmail: string;
  signedRegistrationFilename: string;
  bankDetailsFilename: string;
  miscFilenames: string;
  language: string;
  cardNumber: string;
}

export interface ExistingPatientResult {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  patientCode: string;
  trialCode: string;
}

export interface CheckExistingPatientResult {
  patientMatchedByEmail: ExistingPatientResult;
  patientMatchedByCode: ExistingPatientResult;
}

export interface BankCoverFee {
  thresholdValue: number;
  feeValue: number;
  id: string
}

export interface BeneficiaryDetails {
  id: string;
  firstName: string;
  lastName: string;
  iban: string;
  bankCode: string;
  sortCode: string;
  swift: string;
  accountNumber: string;
  currency: string;
  country: string;
}

export enum PaymentMethod {
  BankTransfer = 0,
  CaxtonCard = 1,
  NmibleCard = 2,
  NmibleBank = 3,
}

export enum BankTransferMethod {
  Manual = 0,
  Automated = 1
}

export class CaxtonTitle {
  public static readonly Mr: string = "Mr";
  public static readonly Mrs: string = "Mrs";
  public static readonly Miss: string = "Miss";
  public static readonly Ms: string = "Ms";
  public static readonly Dr: string = "Dr";
  public static readonly Lord: string = "Lord";
  public static readonly Professor: string = "Professor";
  public static readonly Reverend: string = "Reverend";
  public static readonly Mx: string = "Mx";

  public static all(): string[] {
    return [
      this.Mr,
      this.Mrs,
      this.Miss,
      this.Ms,
      this.Dr,
      this.Lord,
      this.Professor,
      this.Reverend,
      this.Mx
    ]
  }
}

export class Gender {
  public static readonly Male: string = "Male";
  public static readonly Female: string = "Female";
  public static readonly Other: string = "Other";

  public static all(): string[] {
    return [
      this.Male,
      this.Female,
      this.Other,
    ]
  }
}

export class PatientBasicInfo {
  public firstName: string;
  public lastName: string;
  public email: string;
}

export interface AssignCardRequest {
  patientTrialId: string;
  walletId: string;
  caxtonRequest: CaxtonAssignCardRequest;
}

export interface ActivateCardsRequest {
  patientTrialId: string;
  walletId: string;
}

export interface GetCardPinRequest extends ActivateCardsRequest {
}

export interface CaxtonAssignCardRequest {
  email: string;
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  trialCode: string;
}

export interface AssignCardResult {
  onlineAccessGranted: boolean;
}

export interface ActivateCardsResult {
  cardsActivatedSuccessfully: boolean;
  cardsOnlineAccessGranted: boolean;
}

export interface DeletePatientRequest {
  patientId: string;
  closingState: string;
  reason: string;
}

export interface ActivePatientTrialCodesViewModel {
  code: string;
}

export interface CardPinViewModel {
  cardPinImageUrl: string;
}

export class GetWalletBalanceRequest {
  patientTrialId: string;
  walletId?: string;
}

export interface WalletBalancesViewModel {
  balances: WalletBalanceViewModel[];
}

export interface WalletBalanceViewModel {
  value: number;
  symbol: string;
}

export interface UpdateEntityAmountRequest {
  entityId: string;
  amount: number;
}

export interface UpdateEntityCurrencyRequest {
  entityId: string;
  currency: string;
}

export interface UpdateEntityInvoiceNumberRequest {
  entityId: string;
  invoiceNumber: string;
}

export interface CardsExistOnWalletResult {
  cardsExistOnWallet: boolean;
}

export interface UpdatePatientTripInvoicedAmountRequest extends UpdateEntityAmountRequest { }

export interface UpdatePatientTripInvoicedCurrencyRequest extends UpdateEntityCurrencyRequest { }

export interface UpdatePatientTripInvoicedAmountBcRequest extends UpdateEntityAmountRequest { }

export interface UpdatePatientTripQuotedAmountRequest extends UpdateEntityAmountRequest { }

export interface UpdatePatientTripQuotedAmountBcRequest extends UpdateEntityAmountRequest { }

export interface UpdatePatientTripQuotedCurrencyRequest extends UpdateEntityCurrencyRequest { }

export interface UpdatePatientTripInvoiceNumberRequest extends UpdateEntityInvoiceNumberRequest { }

export interface PatientStudyPhaseResult {
  studyPhase: string;
  treatmentPhaseStartDate?: Date;
  addedToTrialDate: Date;
}

export interface PatientRegistrationActionTakenRequest {
  patientRegistrationId: string;
  patientRegistrationAction: PatientRegistrationAction;
}

export enum PatientRegistrationAction {
  Accepted,
  Rejected
}

export interface CreateNmibleCardRequest {
  patientTrialId: string;
  givenName: string;
  middleName?: string;
  familyName: string;
  country: string;
  email: string;
}

export interface AssignNmibleCardRequest {
  sourcePatientTrialId?: string;
  targetPatientTrialId: string;
  sourceOptionId?: string;
}

export interface CreateNmibleCardResponse {
  nmiblePayeeId: string;
  optionId: string;
}

export enum NmiblePaymentOptionType {
  VirtualCard = 0,
  BankAccount = 1,
}

export interface NmiblePaymentOption {
  optionId: string;
  payeeId: string;
  optionType: NmiblePaymentOptionType;
  country: string;
  selected: boolean;
  created: string;
  fields?: { [key: string]: any };
}

export interface CreateNmibleBankAccountRequest {
  patientTrialId: string;
  country: string;
  provider?: string;
  givenName: string;
  middleName?: string;
  familyName: string;
  email: string;
  accountFields: { [key: string]: any };
}

export interface NmibleKeyValueOption {
  key: string;
  value: string;
}

export interface NmiblePaymentMethodSubProperty {
  title: string;
  clientType?: string;
  type?: string;
  order?: number;
  maxLength?: number;
  minLength?: number;
  options?: NmibleKeyValueOption[];
}

export interface NmiblePaymentMethodProperty {
  title: string;
  description?: string;
  propertyName: string;
  pattern?: string;
  type?: string;
  clientType?: string;
  maxLength?: number;
  minLength?: number;
  enum?: NmibleKeyValueOption[];
  properties?: { [key: string]: NmiblePaymentMethodSubProperty };
  requiredFields?: string[];
}

export interface NmibleGetPaymentMethodsResponse {
  title: string;
  paymentType: string;
  provider: string;
  paymentCountry: string;
  requiredFields: string[];
  currency: string;
  methodProviderName: string;
  properties: NmiblePaymentMethodProperty[];
}

export interface NmibleBankAccountDetailsResponse {
  optionId: string;
  country: string;
  fields: { [key: string]: any };
}

export interface NmibleBankAccountSummary {
  patientTrialId: string;
  trialId: string;
  trialCode: string;
  trialName: string;
  optionId: string;
  country: string;
  selected: boolean;
}

export interface GetNmibleCardDetailsResult {
  clientToken: string;
  paymentCardId: string;
  balance?: number;
  currency?: string;
  expirationDate?: string;
}

export interface UpdateCaxtonCardStateResult {
  updateSuccessful: boolean;
  message: string;
}

export enum ExistingPatientAction {
  UseExistingData = 1,
  UseNewData = 2
}